import { c as _c } from "react/compiler-runtime";
import type { PhoneConflictData } from '@/features/negociacaoIndividualWizard/types';
import { descricaoDoConflito } from '@/features/negociacaoIndividualWizard/utils/conflitoNegociacao';
import { statusLabelMap } from '@/features/negociacoes/utils';
import { Button } from '@/shared/components/ui';
import { formatCpfCnpj, formatDateTimeFull } from '@/shared/formatters';
import { formatTelefoneComCodigoPais } from '@/shared/utils';
import { AlertTriangle, Briefcase, Building2, Clock, FileText, Phone, User, X } from 'lucide-react';
interface PhoneConflictModalProps {
  isOpen: boolean;
  conflict: PhoneConflictData | null;
  isClosing: boolean;
  onKeepCurrent: () => void;
  onCloseAndStartNew: () => void;
  /**
   * Subtítulo do cabeçalho. Sem ele, sai do `matchedBy` do conflito (telefone ou CPF). A validação
   * do lote passa o próprio.
   */
  descricao?: string;
  /** O que a pessoa pode fazer. Em conflito de outro workspace encerrar não é opção. */
  instrucao?: string;
}
export function PhoneConflictModal(t0) {
  const $ = _c(79);
  const {
    isOpen,
    conflict,
    isClosing,
    onKeepCurrent,
    onCloseAndStartNew,
    descricao,
    instrucao: t1
  } = t0;
  const instrucao = t1 === undefined ? "Para prosseguir com uma nova proposta, ser\xE1 necess\xE1rio encerrar a negocia\xE7\xE3o anterior. Esta a\xE7\xE3o \xE9 irrevers\xEDvel." : t1;
  if (!isOpen || !conflict) {
    return null;
  }
  const negotiationStatusLabel = statusLabelMap[conflict.status] ?? conflict.status;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="bg-warning-100 flex h-10 w-10 flex-none items-center justify-center rounded-full"><AlertTriangle className="text-warning-700 h-5 w-5" /></div>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h3 className="text-default-font text-lg font-semibold">Negociação em Andamento</h3>;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== conflict.matchedBy || $[3] !== descricao) {
    t4 = descricao ?? descricaoDoConflito(conflict.matchedBy);
    $[2] = conflict.matchedBy;
    $[3] = descricao;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <div className="flex items-start gap-3">{t2}<div className="flex flex-col gap-1">{t3}<p className="text-subtext-color text-sm">{t4}</p></div></div>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <X className="h-5 w-5" />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isClosing || $[9] !== onKeepCurrent) {
    t7 = <button type="button" onClick={onKeepCurrent} disabled={isClosing} aria-label="Fechar modal" className="text-subtext-color flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-60">{t6}</button>;
    $[8] = isClosing;
    $[9] = onKeepCurrent;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = <div className="border-warning-100 bg-warning-50 flex items-start justify-between gap-3 border-b border-solid px-5 py-4">{t5}{t7}</div>;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Building2 className="text-subtext-color h-3.5 w-3.5 flex-none" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== conflict.empresaNome) {
    t10 = <div className="flex min-w-0 items-center gap-2">{t9}<span className="text-subtext-color truncate text-xs font-medium tracking-wide uppercase">{conflict.empresaNome}</span></div>;
    $[15] = conflict.empresaNome;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== negotiationStatusLabel) {
    t11 = <span className="bg-warning-100 text-warning-800 rounded-full px-2 py-0.5 text-xs font-medium">{negotiationStatusLabel}</span>;
    $[17] = negotiationStatusLabel;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11) {
    t12 = <div className="flex flex-wrap items-center justify-between gap-2 border-b border-solid border-neutral-200 bg-neutral-50 px-4 py-2">{t10}{t11}</div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="text-subtext-color flex items-center gap-1.5 text-xs"><FileText className="h-3.5 w-3.5" />ID da Negociação</span>;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== conflict.caseId) {
    t14 = <div className="flex flex-col gap-1 border-b border-solid border-neutral-100 px-4 py-2 sm:border-r">{t13}<span className="text-default-font text-sm font-medium">{conflict.caseId}</span></div>;
    $[23] = conflict.caseId;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="text-subtext-color flex items-center gap-1.5 text-xs"><Clock className="h-3.5 w-3.5" />Data de Criação</span>;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== conflict.createdAt) {
    t16 = formatDateTimeFull(conflict.createdAt) || "\u2014";
    $[26] = conflict.createdAt;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t16) {
    t17 = <div className="flex flex-col gap-1 border-b border-solid border-neutral-100 px-4 py-2">{t15}<span className="text-default-font text-sm font-medium">{t16}</span></div>;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="text-subtext-color flex items-center gap-1.5 text-xs"><User className="h-3.5 w-3.5" />CPF da Pessoa</span>;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== conflict.counterpartyCpfCnpj) {
    t19 = conflict.counterpartyCpfCnpj ? formatCpfCnpj(conflict.counterpartyCpfCnpj) : "\u2014";
    $[31] = conflict.counterpartyCpfCnpj;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t19) {
    t20 = <div className="flex flex-col gap-1 border-b border-solid border-neutral-100 px-4 py-2 sm:border-r">{t18}<span className="text-default-font text-sm font-medium">{t19}</span></div>;
    $[33] = t19;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <span className="text-subtext-color flex items-center gap-1.5 text-xs"><Phone className="h-3.5 w-3.5" />Telefone</span>;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== conflict.counterpartyPhone) {
    t22 = formatTelefoneComCodigoPais(conflict.counterpartyPhone);
    $[36] = conflict.counterpartyPhone;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t22) {
    t23 = <div className="flex flex-col gap-1 border-b border-solid border-neutral-100 px-4 py-2">{t21}<span className="text-default-font text-sm font-medium">{t22}</span></div>;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span className="text-subtext-color flex items-center gap-1.5 text-xs"><User className="h-3.5 w-3.5" />Criado por</span>;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  const t25 = conflict.createdByName || "\u2014";
  let t26;
  if ($[41] !== t25) {
    t26 = <div className="flex flex-col gap-1 px-4 py-2 sm:border-r sm:border-solid sm:border-neutral-100">{t24}<span className="text-default-font text-sm font-medium">{t25}</span></div>;
    $[41] = t25;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <span className="text-subtext-color flex items-center gap-1.5 text-xs"><Briefcase className="h-3.5 w-3.5" />Agente</span>;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  const t28 = conflict.agentName || "\u2014";
  let t29;
  if ($[44] !== t28) {
    t29 = <div className="flex flex-col gap-1 px-4 py-2">{t27}<span className="text-default-font text-sm font-medium">{t28}</span></div>;
    $[44] = t28;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  let t30;
  if ($[46] !== t14 || $[47] !== t17 || $[48] !== t20 || $[49] !== t23 || $[50] !== t26 || $[51] !== t29) {
    t30 = <div className="grid grid-cols-1 sm:grid-cols-2">{t14}{t17}{t20}{t23}{t26}{t29}</div>;
    $[46] = t14;
    $[47] = t17;
    $[48] = t20;
    $[49] = t23;
    $[50] = t26;
    $[51] = t29;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== t12 || $[54] !== t30) {
    t31 = <div className="bg-default-background overflow-hidden rounded-lg border border-solid border-neutral-200 shadow-sm">{t12}{t30}</div>;
    $[53] = t12;
    $[54] = t30;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  let t32;
  if ($[56] !== instrucao) {
    t32 = <p className="text-subtext-color px-2 text-center text-xs">{instrucao}</p>;
    $[56] = instrucao;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== conflict.blockedReason || $[59] !== conflict.canClose) {
    t33 = !conflict.canClose && conflict.blockedReason && <div className="border-error-200 bg-error-50 rounded-lg border border-solid px-3 py-2"><p className="text-error-700 text-center text-xs">{conflict.blockedReason}</p></div>;
    $[58] = conflict.blockedReason;
    $[59] = conflict.canClose;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  const t34 = !conflict.canClose;
  let t35;
  if ($[61] !== isClosing || $[62] !== onCloseAndStartNew || $[63] !== t34) {
    t35 = <Button variant="brand-primary" onClick={onCloseAndStartNew} loading={isClosing} disabled={t34} className="h-10 w-full rounded-lg text-sm font-medium">Encerrar anterior e iniciar nova</Button>;
    $[61] = isClosing;
    $[62] = onCloseAndStartNew;
    $[63] = t34;
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  let t36;
  if ($[65] !== isClosing || $[66] !== onKeepCurrent) {
    t36 = <Button variant="neutral-secondary" onClick={onKeepCurrent} disabled={isClosing} className="h-10 w-full rounded-lg text-sm font-medium">Manter negociação atual</Button>;
    $[65] = isClosing;
    $[66] = onKeepCurrent;
    $[67] = t36;
  } else {
    t36 = $[67];
  }
  let t37;
  if ($[68] !== t35 || $[69] !== t36) {
    t37 = <div className="flex flex-col gap-2">{t35}{t36}</div>;
    $[68] = t35;
    $[69] = t36;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  let t38;
  if ($[71] !== t31 || $[72] !== t32 || $[73] !== t33 || $[74] !== t37) {
    t38 = <div className="flex flex-col gap-4 px-5 py-4">{t31}{t32}{t33}{t37}</div>;
    $[71] = t31;
    $[72] = t32;
    $[73] = t33;
    $[74] = t37;
    $[75] = t38;
  } else {
    t38 = $[75];
  }
  let t39;
  if ($[76] !== t38 || $[77] !== t8) {
    t39 = <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/30 p-4 sm:items-center"><div className="w-full max-w-lg overflow-hidden rounded-xl border border-solid border-neutral-200 bg-neutral-50 shadow-2xl" role="dialog" aria-modal="true" aria-label={"Conflito de negocia\xE7\xE3o ativa"}>{t8}{t38}</div></div>;
    $[76] = t38;
    $[77] = t8;
    $[78] = t39;
  } else {
    t39 = $[78];
  }
  return t39;
}